import { getValidAuthorizationHeader } from '../auth/cognito';
import { getReferralsApiUrl } from '../config/api';
import { ensureSignInUserInfo } from './signinUserInfo';
import { DEFAULT_REFERRAL_FILTERS } from '../types/referral';
async function getUserId() {
    const userInfo = await ensureSignInUserInfo();
    console.log('userInfo', userInfo);
    const userId = userInfo.detail?.id;
    if (typeof userId !== 'number') {
        throw new Error('Could not determine signed-in user id.');
    }
    return userId;
}
export async function fetchReferralOverview(options) {
    const authorization = await getValidAuthorizationHeader();
    const createdBy = typeof options?.createdBy === 'number' &&
        Number.isFinite(options.createdBy) &&
        options.createdBy > 0
        ? options.createdBy
        : null;
    const range = options?.range === '1m' || options?.range === '1w' || options?.range === '6m'
        ? options.range
        : '6m';
    const response = await fetch(getReferralsApiUrl('/referrals/overview'), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: authorization,
        },
        body: JSON.stringify({
            ...(createdBy != null ? { createdBy } : {}),
            range,
        }),
    });
    const rawText = await response.text();
    let payload;
    try {
        payload = JSON.parse(rawText);
    }
    catch {
        throw new Error(`Invalid JSON response (${response.status}): ${rawText.slice(0, 200)}`);
    }
    if (!response.ok || payload.error || !payload.overview) {
        throw new Error(payload.error ?? `Request failed (${response.status})`);
    }
    return payload.overview;
}
export async function fetchOverviewUsers() {
    const authorization = await getValidAuthorizationHeader();
    const response = await fetch(getReferralsApiUrl('/referrals/overview/users'), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: authorization,
        },
        body: JSON.stringify({}),
    });
    const rawText = await response.text();
    let payload;
    try {
        payload = JSON.parse(rawText);
    }
    catch {
        throw new Error(`Invalid JSON response (${response.status}): ${rawText.slice(0, 200)}`);
    }
    if (!response.ok || payload.error || !Array.isArray(payload.users)) {
        throw new Error(payload.error ?? `Request failed (${response.status})`);
    }
    return payload.users;
}
export async function fetchReferrals(pageIndex, pageSize, dateSortOrder = 'DESC', filters = DEFAULT_REFERRAL_FILTERS, dateSortColumn = 'created') {
    const userId = await getUserId();
    const authorization = await getValidAuthorizationHeader();
    const response = await fetch(getReferralsApiUrl('/referrals/list'), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: authorization,
        },
        body: JSON.stringify({
            userId,
            pageIndex,
            pageSize,
            dateSortOrder,
            dateSortColumn,
            filters,
        }),
    });
    const rawText = await response.text();
    let payload;
    try {
        payload = JSON.parse(rawText);
    }
    catch {
        throw new Error(`Invalid JSON response (${response.status}): ${rawText.slice(0, 200)}`);
    }
    if (!response.ok || payload.error || !payload.result) {
        throw new Error(payload.error ?? `Request failed (${response.status})`);
    }
    return payload.result;
}
export async function fetchReferralDetail(id) {
    const userId = await getUserId();
    const authorization = await getValidAuthorizationHeader();
    const response = await fetch(getReferralsApiUrl('/referrals/detail'), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: authorization,
        },
        body: JSON.stringify({ userId, id }),
    });
    const rawText = await response.text();
    let payload;
    try {
        payload = JSON.parse(rawText);
    }
    catch {
        throw new Error(`Invalid JSON response (${response.status}): ${rawText.slice(0, 200)}`);
    }
    if (!response.ok || payload.error || !payload.detail) {
        throw new Error(payload.error ?? `Request failed (${response.status})`);
    }
    return payload.detail;
}
export async function createReferralUpdatedInfo(incidentId, description) {
    const senderId = await getUserId();
    const authorization = await getValidAuthorizationHeader();
    const response = await fetch(getReferralsApiUrl('/referrals/updated-info'), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: authorization,
        },
        body: JSON.stringify({ incidentId, description, senderId }),
    });
    const rawText = await response.text();
    let payload;
    try {
        payload = JSON.parse(rawText);
    }
    catch {
        throw new Error(`Invalid JSON response (${response.status}): ${rawText.slice(0, 200)}`);
    }
    if (!response.ok || payload.error) {
        throw new Error(payload.error ?? `Request failed (${response.status})`);
    }
}
/** Update an attached form's answers (survey JSON omitted so it stays as-is). */
export async function updateReferralAttachedForm(request) {
    const authorization = await getValidAuthorizationHeader();
    const response = await fetch(getReferralsApiUrl('/referrals/attached-form'), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: authorization,
        },
        body: JSON.stringify({
            id: request.id,
            formData: request.formData,
            formTitle: request.formTitle,
            formId: request.formId,
            incidentId: request.incidentId,
            survey: request.survey ?? null,
        }),
    });
    const rawText = await response.text();
    let payload;
    try {
        payload = JSON.parse(rawText);
    }
    catch {
        throw new Error(`Invalid JSON response (${response.status}): ${rawText.slice(0, 200)}`);
    }
    if (!response.ok || payload.error) {
        throw new Error(payload.error ?? `Request failed (${response.status})`);
    }
}
/** Create an attached form on an existing incident. */
export async function createReferralAttachedForm(request) {
    const authorization = await getValidAuthorizationHeader();
    const response = await fetch(getReferralsApiUrl('/referrals/attached-form'), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: authorization,
        },
        body: JSON.stringify({
            incidentId: request.incidentId,
            formId: request.formId,
            formTitle: request.formTitle,
            survey: request.survey ?? null,
            formData: request.formData ?? {},
        }),
    });
    const rawText = await response.text();
    let payload;
    try {
        payload = JSON.parse(rawText);
    }
    catch {
        throw new Error(`Invalid JSON response (${response.status}): ${rawText.slice(0, 200)}`);
    }
    if (!response.ok || payload.error) {
        throw new Error(payload.error ?? `Request failed (${response.status})`);
    }
}
/** Render one saved form to PDF, store in S3, and attach to the incident. */
export async function snapshotReferralFormPdf(request) {
    const authorization = await getValidAuthorizationHeader();
    const response = await fetch(getReferralsApiUrl('/referrals/form-pdf-snapshot'), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: authorization,
        },
        body: JSON.stringify({
            incidentId: request.incidentId,
            fileName: request.fileName,
            formId: request.formId ?? null,
            surveyJson: request.surveyJson ?? null,
            formData: request.formData ?? {},
        }),
    });
    const rawText = await response.text();
    let payload;
    try {
        payload = JSON.parse(rawText);
    }
    catch {
        throw new Error(`Invalid JSON response (${response.status}): ${rawText.slice(0, 200)}`);
    }
    if (!response.ok || payload.error) {
        throw new Error(payload.error ?? `PDF snapshot failed (${response.status})`);
    }
    return { fileName: payload.result?.fileName ?? request.fileName };
}
/** Download an attachment via the S3 Download File workflow (bucket + path → base64). */
export async function fetchAttachmentDownload(bucket, path, fileName) {
    const authorization = await getValidAuthorizationHeader();
    const response = await fetch(getReferralsApiUrl('/referrals/attachment/download'), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: authorization,
        },
        body: JSON.stringify({
            path,
            fileName,
            ...(bucket.trim() ? { bucket: bucket.trim() } : {}),
        }),
    });
    const rawText = await response.text();
    let payload;
    try {
        payload = JSON.parse(rawText);
    }
    catch {
        throw new Error(`Invalid JSON response (${response.status}): ${rawText.slice(0, 200)}`);
    }
    if (!response.ok || payload.error || !payload.result?.content?.trim()) {
        throw new Error(payload.error ?? `Request failed (${response.status})`);
    }
    return payload.result;
}
/** Turn a base64 attachment payload into a browser file download. */
export function saveBase64File(file) {
    const binary = atob(file.content);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
    }
    const blob = new Blob([bytes], {
        type: file.contentType || 'application/octet-stream',
    });
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = file.fileName || 'attachment';
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}
